/**
 * GoogleAdsSection Component
 * Displays Google Ads-specific data: Ad Groups, Keywords, and Search Terms
 */

import React, { useState, useEffect } from 'react';
import { Loader2, ChevronDown, ChevronRight, Search, Tag, Layers } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useCommandCenter } from '../../contexts/CommandCenterContext';
import { formatNumber, formatPercent } from '../../utils/helpers';
import { formatCurrency } from '../../utils/objectiveHelpers';

const API_BASE = 'http://localhost:3001';

const GoogleAdsSection = ({ campaign }) => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();
    const { dateRange } = useCommandCenter();

    const [activeTab, setActiveTab] = useState('adGroups');
    const [adGroups, setAdGroups] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [searchTerms, setSearchTerms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch data when tab changes or campaign changes
    useEffect(() => {
        if (!currentUser || !selectedBrand || !campaign) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    campaignId: campaign.platformCampaignId,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate
                });

                let endpoint;
                switch (activeTab) {
                    case 'adGroups':
                        endpoint = `${API_BASE}/api/campaigns/google/ad-groups?${params}`;
                        break;
                    case 'keywords':
                        endpoint = `${API_BASE}/api/campaigns/google/keywords?${params}`;
                        break;
                    case 'searchTerms':
                        endpoint = `${API_BASE}/api/campaigns/google/search-terms?${params}`;
                        break;
                    default:
                        return;
                }

                const response = await fetch(endpoint);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to fetch data');
                }

                switch (activeTab) {
                    case 'adGroups':
                        setAdGroups(data.adGroups || []);
                        break;
                    case 'keywords':
                        setKeywords(data.keywords || []);
                        break;
                    case 'searchTerms':
                        setSearchTerms(data.searchTerms || []);
                        break;
                }
            } catch (err) {
                console.error(`Error fetching ${activeTab}:`, err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [activeTab, campaign, currentUser, selectedBrand, dateRange]);

    const tabs = [
        { id: 'adGroups', label: 'Ad Groups', icon: Layers, count: adGroups.length },
        { id: 'keywords', label: 'Keywords', icon: Tag, count: keywords.length },
        { id: 'searchTerms', label: 'Search Terms', icon: Search, count: searchTerms.length }
    ];

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-8)',
                    color: 'var(--color-text-secondary)'
                }}>
                    <Loader2 size={24} className="spinning" style={{ marginRight: 'var(--space-2)' }} />
                    Loading...
                </div>
            );
        }

        if (error) {
            return (
                <div style={{
                    padding: 'var(--space-4)',
                    color: 'var(--color-error)',
                    background: 'var(--color-error-alpha)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {error}
                </div>
            );
        }

        switch (activeTab) {
            case 'adGroups':
                return <AdGroupsTable data={adGroups} />;
            case 'keywords':
                return <KeywordsTable data={keywords} />;
            case 'searchTerms':
                return <SearchTermsTable data={searchTerms} />;
            default:
                return null;
        }
    };

    return (
        <div>
            {/* Tabs */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-1)',
                borderBottom: '1px solid var(--color-border)',
                marginBottom: 'var(--space-4)'
            }}>
                {tabs.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                                color: isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: isActive ? 600 : 400,
                                marginBottom: '-1px',
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            <Icon size={16} />
                            {tab.label}
                            {tab.count > 0 && (
                                <span style={{
                                    background: isActive ? 'var(--color-primary)' : 'var(--color-border)',
                                    color: isActive ? 'white' : 'var(--color-text-secondary)',
                                    padding: '1px 6px',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)'
                                }}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            {renderContent()}

            <style>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

// Ad Groups Table
const AdGroupsTable = ({ data }) => {
    if (data.length === 0) {
        return (
            <div style={{
                textAlign: 'center',
                padding: 'var(--space-6)',
                color: 'var(--color-text-tertiary)'
            }}>
                No ad groups found for this campaign
            </div>
        );
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th style={thStyle}>Ad Group</th>
                        <th style={thStyle}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Impressions</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Clicks</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CTR</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Spend</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Conv.</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CPA</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((group, idx) => (
                        <tr key={group.id || idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={tdStyle}>
                                <div style={{ fontWeight: 500 }}>{group.name}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                    {group.type}
                                </div>
                            </td>
                            <td style={tdStyle}>
                                <StatusBadge status={group.status} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(group.metrics?.impressions || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(group.metrics?.clicks || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{(group.metrics?.ctr || 0).toFixed(2)}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(group.metrics?.spend || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(group.metrics?.conversions || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(group.metrics?.cpa || 0)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Keywords Table
const KeywordsTable = ({ data }) => {
    if (data.length === 0) {
        return (
            <div style={{
                textAlign: 'center',
                padding: 'var(--space-6)',
                color: 'var(--color-text-tertiary)'
            }}>
                No keywords found for this campaign
            </div>
        );
    }

    const getMatchTypeLabel = (matchType) => {
        const labels = {
            'EXACT': 'Exact',
            'PHRASE': 'Phrase',
            'BROAD': 'Broad'
        };
        return labels[matchType] || matchType;
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th style={thStyle}>Keyword</th>
                        <th style={thStyle}>Match Type</th>
                        <th style={thStyle}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Impr.</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Clicks</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CTR</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Spend</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CPC</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Conv.</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((kw, idx) => (
                        <tr key={kw.id || idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={tdStyle}>
                                <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>{kw.text}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                    {kw.adGroupName}
                                </div>
                            </td>
                            <td style={tdStyle}>
                                <span style={{
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-sm)',
                                    background: 'var(--color-surface-hover)',
                                    fontSize: 'var(--font-size-xs)'
                                }}>
                                    {getMatchTypeLabel(kw.matchType)}
                                </span>
                            </td>
                            <td style={tdStyle}>
                                <StatusBadge status={kw.status} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(kw.metrics?.impressions || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(kw.metrics?.clicks || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{(kw.metrics?.ctr || 0).toFixed(2)}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(kw.metrics?.spend || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(kw.metrics?.cpc || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(kw.metrics?.conversions || 0)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Search Terms Table
const SearchTermsTable = ({ data }) => {
    if (data.length === 0) {
        return (
            <div style={{
                textAlign: 'center',
                padding: 'var(--space-6)',
                color: 'var(--color-text-tertiary)'
            }}>
                No search terms found for this campaign
            </div>
        );
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th style={thStyle}>Search Term</th>
                        <th style={thStyle}>Matched Keyword</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Impressions</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Clicks</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>CTR</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Spend</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Conv.</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((term, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={tdStyle}>
                                <div style={{ fontWeight: 500 }}>{term.searchTerm}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                    {term.adGroupName}
                                </div>
                            </td>
                            <td style={tdStyle}>
                                <span style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                                    {term.matchedKeyword || '-'}
                                </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(term.metrics?.impressions || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(term.metrics?.clicks || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{(term.metrics?.ctr || 0).toFixed(2)}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(term.metrics?.spend || 0)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(term.metrics?.conversions || 0)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
    const config = {
        'ACTIVE': { color: 'var(--color-success)', bg: 'var(--color-success-alpha)' },
        'PAUSED': { color: 'var(--color-warning)', bg: 'var(--color-warning-alpha)' },
        'ENABLED': { color: 'var(--color-success)', bg: 'var(--color-success-alpha)' },
        'REMOVED': { color: 'var(--color-error)', bg: 'var(--color-error-alpha)' }
    };

    const style = config[status] || { color: 'var(--color-text-secondary)', bg: 'var(--color-surface-hover)' };

    return (
        <span style={{
            padding: '2px 8px',
            borderRadius: 'var(--radius-full)',
            background: style.bg,
            color: style.color,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500
        }}>
            {status}
        </span>
    );
};

// Table styles
const thStyle = {
    padding: 'var(--space-2) var(--space-3)',
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    whiteSpace: 'nowrap'
};

const tdStyle = {
    padding: 'var(--space-2) var(--space-3)',
    color: 'var(--color-text-primary)'
};

export default GoogleAdsSection;
